import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import FieldDefinitionLabel from 'registry/field/FieldDefinitionLabel';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import Col from 'ui-library/bs/Col';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import BaseValidationModal, {
  ValidationRuleModalProps,
} from 'validation_rules/components/simple_ui/BaseValidationRuleModal';

import useSimpleRules, {SimpleRulesState, ValidationRuleConstantInput} from './useSimpleRules';
import {getInputForSimpleRule} from './utils';

const BOOLEAN_FIELD_EQUALS_TEMPLATE_ID = 'svdrltmpl_booleanfieldequals';
const BOOLEAN_FIELD_EQUALS_INPUT_KEY = 'value2';

interface BooleanFieldModalProps extends ValidationRuleModalProps {
  mustEqualValue: boolean | null;
  fieldDefinitionLabel: JSX.Element;
  handleUpdateSimpleRule: (
    validationRuleTemplateId: string,
    constantInputs: ValidationRuleConstantInput
  ) => void;
  handleRemoveSimpleRule: (validationRuleTemplateId: string) => void;
}

export default function BooleanFieldModal(props: BooleanFieldModalProps): JSX.Element {
  const emptyString = '';
  return (
    <BaseValidationModal {...props} disabled={false}>
      <Row>
        <Label xs={12} sm={12} md={12} lg={12}>
          Must equal
        </Label>
        <Col xs={12} sm={12} md={12} lg={12}>
          <SelectDropdown
            displayName={props.mustEqualValue == null ? '' : props.mustEqualValue ? 'True' : 'False'}
            menu={
              <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={props.mustEqualValue}>
                <Option
                  key="Null"
                  value={null}
                  onSelect={() => {
                    props.handleRemoveSimpleRule(BOOLEAN_FIELD_EQUALS_TEMPLATE_ID);
                  }}
                >
                  {emptyString}
                </Option>
                <Option
                  key="True"
                  value={true}
                  onSelect={() => {
                    props.handleUpdateSimpleRule(BOOLEAN_FIELD_EQUALS_TEMPLATE_ID, {
                      [BOOLEAN_FIELD_EQUALS_INPUT_KEY]: true,
                    });
                  }}
                >
                  True
                </Option>
                <Option
                  key="False"
                  value={false}
                  onSelect={() => {
                    props.handleUpdateSimpleRule(BOOLEAN_FIELD_EQUALS_TEMPLATE_ID, {
                      [BOOLEAN_FIELD_EQUALS_INPUT_KEY]: false,
                    });
                  }}
                >
                  False
                </Option>
              </Menu>
            }
            dataTestAttrs={{component: 'BooleanFieldModal', element: 'mustEqualDropdown'}}
          />
        </Col>
      </Row>
    </BaseValidationModal>
  );
}

interface BooleanFieldModalContainerProps {
  schemaField: SchemaField;
  onRequestSubmit: (result: SimpleRulesState) => void;
  onRequestClose: () => void;
  enableMultiSelectOption: boolean;
}

export function BooleanFieldModalContainer(props: BooleanFieldModalContainerProps): JSX.Element {
  const [simpleRulesState, simpleRulesActions] = useSimpleRules(props.schemaField);
  const simpleRules = simpleRulesState.simpleRules;
  const legacySimpleRules = simpleRulesState.legacySimpleRules;

  const mustEqualValue = getInputForSimpleRule(
    simpleRules,
    BOOLEAN_FIELD_EQUALS_TEMPLATE_ID,
    BOOLEAN_FIELD_EQUALS_INPUT_KEY
  );

  assert(
    mustEqualValue == null || typeof mustEqualValue === 'boolean',
    `Expected mustEqualValue to be a boolean or null, got ${typeof mustEqualValue}`
  );

  const fieldDefinitionLabel = (
    <Chip
      className={css(styles.fieldDefinitionLabel)}
      text={<FieldDefinitionLabel field={props.schemaField} schemaFieldSelectors={[]} />}
      size={ChipSize.MINI}
    />
  );

  return (
    <BooleanFieldModal
      mustEqualValue={mustEqualValue}
      fieldDefinitionLabel={fieldDefinitionLabel}
      handleUpdateSimpleRule={simpleRulesActions.handleUpdateSimpleRule}
      handleRemoveSimpleRule={simpleRulesActions.handleRemoveSimpleRule}
      onRequestSubmit={() => {
        props.onRequestSubmit(simpleRulesState);
      }}
      onRequestClose={props.onRequestClose}
      legacySimpleRules={legacySimpleRules}
      handleUpdateLegacyRules={simpleRulesActions.handleUpdateLegacySimpleRules}
    />
  );
}

const styles = StyleSheet.create({
  fieldDefinitionLabel: {
    marginRight: 0,
  },
});
